import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { CollapsibleCard } from '~/eds/src/components/Card/subcomponents/CollapsibleCard';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Progress } from '~/eds/src/components/Progress/Progress';
import { Text } from '~/eds/src/components/Text/Text';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { BOOST_CYCLE } from '../../constants';
import { FieldModel } from '../../fields/types';
import { getNewRatedTestCases } from '../../utils/testcase';

interface Props {
  modelVersion: FieldModel;
  readOnly: boolean;
}
export const BoostTrigger = ({ modelVersion, readOnly }: Props) => {
  const intl = useIntl();
  const enableUnratedBoost = useFlag(FlagType.DocumentXRayUnratedBoost);
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const dispatch = useDispatch();

  const { id: modelId, testCases, version } = modelVersion;

  const [optimizePromptModel, { isLoading: isTriggeringOptimizeRun }] =
    api.endpoints.optimizePromptModel.useMutation();

  const {
    data: optimizeTestCases = [],
    isFetching: isLoadingOptimizeTestCases,
  } = api.endpoints.getPromptModelOptimizeTestCases.useQuery({
    modelId,
    highlightMethod,
  });

  const ratedCount = getNewRatedTestCases(optimizeTestCases, testCases).length;

  const disableBoost = readOnly
    ? true
    : enableUnratedBoost
      ? false
      : ratedCount < BOOST_CYCLE;

  const [hideBoost, setHideBoost] = useState(disableBoost);

  // hide/unhide boost based on if it is disabled
  useEffect(() => setHideBoost(disableBoost), [disableBoost]);

  const handleOptimize = () => {
    optimizePromptModel({ modelId, version, highlightMethod })
      .unwrap()
      .then(() => {
        // TODO: no need to persist in redux
        dispatch(
          slices.xRay.actions.setOptimize({
            modelId,
            version,
          }),
        );
      });
  };

  const boostAction = {
    disabled: disableBoost,
    level: 'ai' as const,
    text: intl.formatMessage({
      id: 'BoostTrigger.boost',
      defaultMessage: 'Boost',
      description: 'Button text for AI boost action',
    }),
    onClick: handleOptimize,
    isLoading: isTriggeringOptimizeRun,
  };

  const optimizeInstructions = (
    <Layout align="center" direction="column" preset="l">
      <Text textAlign="center">
        {intl.formatMessage(
          {
            id: 'BoostTrigger.boostDescription',
            defaultMessage:
              'AI Boost looks for optimizations in the model and instructions to improve its accuracy. You can unlock an AI Boost after rating {ratingRequirement} documents.',
            description:
              'Description of AI boost functionality. Example: ratingRequirement = "50"',
          },
          {
            ratingRequirement: enableUnratedBoost
              ? intl.formatMessage({
                  id: 'BoostTrigger.anyNumber',
                  defaultMessage: 'any number of',
                  description:
                    'Text for when any number of ratings is required',
                })
              : BOOST_CYCLE,
          },
        )}
      </Text>
      <Button {...boostAction} />
    </Layout>
  );

  if (isLoadingOptimizeTestCases) {
    return null;
  }

  return (
    <CollapsibleCard
      header={{
        actions: hideBoost && !disableBoost ? [boostAction] : [],
        calloutContent: disableBoost ? (
          <Progress
            value={ratedCount}
            max={BOOST_CYCLE}
            mode="donut"
            name="boost-progress"
            status="ai"
            statsLabel={{
              formatLabel: (value, max) => `${value}/${max}`,
              variant: 'body-medium',
            }}
          />
        ) : undefined,
        icon: {
          icon: 'ai' as const,
          color: 'status.ai' as const,
        },
        size: 'l',
        title: intl.formatMessage({
          id: 'BoostTrigger.unlockAiBoost',
          defaultMessage: 'Unlock an AI Boost',
          description: 'Title for AI boost card',
        }),
      }}
      isCollapsed={hideBoost}
      name="Unlock an AI Boost"
      status="ai"
      onToggle={setHideBoost}
    >
      {optimizeInstructions}
    </CollapsibleCard>
  );
};
